import { useEffect, useState } from "react";
import { Button, Card, Form, InputGroup, Spinner, Table } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { useTranslate } from "../hooks/LanguageProvider";

const backendUrl = process.env.REACT_APP_BACKEND_URL || "http://localhost:5002";

type ExpertUser = {
  id_user: number;
  id_role: number;
  role_name: string;
  nom_user: string | null;
  prenom_user: string | null;
  company: string | null;
  email_user: string | null;
  username_user: string | null;
  wilaya: string | null;
  phone_user: string | null;
  date_creation_user: string | null;
};

const valueOrDash = (value?: string | number | null) => {
  const text = String(value ?? "").trim();
  return text || "-";
};

export function Experts() {
  const { translate } = useTranslate();
  const navigate = useNavigate();
  const [experts, setExperts] = useState<ExpertUser[]>([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);

  const loadExperts = async (nextSearch = search) => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: "1",
        limit: "500",
        sortColumn: "id_user",
        sortOrder: "DESC",
        search: nextSearch,
        type: "1",
        roleId: "4",
      });

      const [listResponse, totalResponse] = await Promise.all([
        fetch(`${backendUrl}/api/users?${params.toString()}`, { mode: "cors" }),
        fetch(`${backendUrl}/api/users/total?${params.toString()}`, {
          mode: "cors",
        }),
      ]);

      const listData = await listResponse.json();
      const totalData = await totalResponse.json();

      setExperts(Array.isArray(listData) ? listData : []);
      setTotal(Number(totalData?.[0]?.total || 0));
    } catch (error) {
      console.error("Erreur chargement experts:", error);
      setExperts([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadExperts("");
  }, []);

  const handleSearchChange = (value: string) => {
    setSearch(value);
    loadExperts(value);
  };

  return (
    <div className="container-fluid py-4">
      <Card className="border-0 shadow-sm" style={{ borderRadius: 14 }}>
        <Card.Body>
          <div className="d-flex flex-wrap justify-content-between align-items-center gap-3 mb-4">
            <div>
              <h4 className="mb-1">
                <i className="las la-user-tie mr-2"></i>
                {translate("Experts")} ({total})
              </h4>
              <p className="text-muted mb-0">
                Comptes avec role expert, separes des OEM et fournisseurs.
              </p>
            </div>

            <InputGroup style={{ maxWidth: 360 }}>
              <InputGroup.Text>
                <i className="las la-search"></i>
              </InputGroup.Text>
              <Form.Control
                value={search}
                placeholder={translate("Search")}
                onChange={(event) => handleSearchChange(event.target.value)}
              />
            </InputGroup>
          </div>

          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" />
            </div>
          ) : (
            <div className="table-responsive">
              <Table hover className="align-middle mb-0">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>{translate("Company")}</th>
                    <th>{translate("Email")}</th>
                    <th>{translate("Phone")}</th>
                    <th>{translate("Wilaya")}</th>
                    <th>{translate("Role")}</th>
                    <th className="text-center">{translate("Actions")}</th>
                  </tr>
                </thead>
                <tbody>
                  {experts.length ? (
                    experts.map((expert) => (
                      <tr key={expert.id_user}>
                        <td>{expert.id_user}</td>
                        <td>
                          <strong>
                            {valueOrDash(expert.company || expert.username_user)}
                          </strong>
                          <div className="text-muted small">
                            {valueOrDash(expert.nom_user)} {valueOrDash(expert.prenom_user)}
                          </div>
                        </td>
                        <td>{valueOrDash(expert.email_user)}</td>
                        <td>{valueOrDash(expert.phone_user)}</td>
                        <td>{valueOrDash(expert.wilaya)}</td>
                        <td>
                          <span className="badge badge-dark">
                            {valueOrDash(expert.role_name || "Expert")}
                          </span>
                        </td>
                        <td className="text-center">
                          <Button
                            size="sm"
                            variant="dark"
                            onClick={() =>
                              navigate(`/expert-profile/${expert.id_user}`)
                            }
                          >
                            <i className="las la-user-tie mr-1"></i>
                            {translate("Profile")}
                          </Button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={7} className="text-center text-muted py-4">
                        Aucun compte expert trouve.
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>
          )}
        </Card.Body>
      </Card>
    </div>
  );
}
